/**
 * Collection Letter Markup Service
 * API client for collection letter analysis, markup, and review.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('fundelite_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Safe error extraction from response
const handleApiError = async (response, defaultMessage) => {
  let errorMessage = defaultMessage;
  try {
    const cloned = response.clone();
    const error = await cloned.json();
    errorMessage = error.detail || errorMessage;
  } catch (e) {
    // JSON parsing failed, use default message
  }
  throw new Error(errorMessage);
};

/**
 * Upload a collection letter document
 */
export const uploadDocument = async (file, caseId = null, clientId = null) => {
  const token = localStorage.getItem('fundelite_token');
  const formData = new FormData();
  formData.append('file', file);
  if (caseId) formData.append('case_id', caseId);
  if (clientId) formData.append('client_id', clientId);
  
  const response = await fetch(`${API_URL}/api/collection-markup/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Upload failed');
  }
  
  return response.json();
};

/**
 * List user's documents
 */
export const listDocuments = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.case_id) queryParams.append('case_id', params.case_id);
  if (params.status) queryParams.append('status', params.status);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  
  const response = await fetch(
    `${API_URL}/api/collection-markup/documents?${queryParams.toString()}`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to list documents');
  }
  
  return response.json();
};

/**
 * Get document details with findings
 */
export const getDocument = async (documentId) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/documents/${documentId}`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get document');
  }
  
  return response.json();
};

/**
 * Delete a document
 */
export const deleteDocument = async (documentId) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/documents/${documentId}`,
    { 
      method: 'DELETE',
      headers: getAuthHeaders() 
    }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to delete document');
  }
  
  return response.json();
};

/**
 * Start document analysis
 */
export const startAnalysis = async (documentId, forceReanalyze = false) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/documents/${documentId}/analyze`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ force_reanalyze: forceReanalyze })
    }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to start analysis');
  }
  
  return response.json();
};

/**
 * Poll analysis status
 */
export const getAnalysisStatus = async (jobId) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/analyze/status/${jobId}`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get analysis status');
  }
  
  return response.json();
};

/**
 * Poll analysis until complete
 */
export const pollAnalysisUntilComplete = async (jobId, onProgress = null, maxWaitMs = 120000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    const status = await getAnalysisStatus(jobId);
    
    if (onProgress) {
      onProgress(status);
    }
    
    if (status.status === 'complete') {
      return status;
    }
    
    if (status.status === 'failed') {
      throw new Error(status.error || 'Analysis failed');
    }
    
    // Wait 2 seconds before polling again
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
  
  throw new Error('Analysis timed out');
};

/**
 * Get document preview image
 */
export const getDocumentPreview = async (documentId, page = 1) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/documents/${documentId}/preview?page=${page}`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get preview');
  }
  
  return response.json();
};

/**
 * Update a finding
 */
export const updateFinding = async (findingId, updates) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/findings/${findingId}`,
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to update finding');
  }
  
  return response.json();
};

/**
 * Merge findings (Pro/Agency only)
 */
export const mergeFindings = async (findingIds, mergedLabel, mergedExplanation = null) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/findings/merge`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        finding_ids: findingIds,
        merged_label: mergedLabel,
        merged_explanation: mergedExplanation
      })
    }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to merge findings');
  }
  
  return response.json();
};

/**
 * Add manual finding (Pro/Agency only)
 */
export const addManualFinding = async (documentId, finding) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/findings/manual`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        document_id: documentId,
        ...finding
      })
    }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to add finding');
  }
  
  return response.json();
};

/**
 * Export document
 */
export const exportDocument = async (documentId, format = 'json', options = {}) => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/documents/${documentId}/export`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        export_format: format,
        include_dismissed: options.includeDismissed || false,
        include_raw_findings: options.includeRawFindings || false,
        branded: options.branded || false
      })
    }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to export');
  }
  
  return response.json();
};

/**
 * Get issue library
 */
export const getIssueLibrary = async () => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/issue-library`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get issue library');
  }
  
  return response.json();
};

/**
 * Get user permissions
 */
export const getPermissions = async () => {
  const response = await fetch(
    `${API_URL}/api/collection-markup/permissions`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get permissions');
  }
  
  return response.json();
};


// ==================== REVIEWED EXPORT FUNCTIONS ====================

/**
 * Create an immutable reviewed export snapshot
 * Original document is NEVER modified
 */
export const createReviewedExport = async (documentId, annotations, options = {}) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/exports`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      annotations,
      include_dismissed: options.includeDismissed || false,
      include_ai_analysis: options.includeAiAnalysis !== false
    })
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Export creation failed');
  }
  
  return response.json();
};

/**
 * List all reviewed exports for a document
 */
export const listReviewedExports = async (documentId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/exports`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to list exports');
  }
  
  return response.json();
};

/**
 * Download a reviewed export PDF
 */
export const downloadReviewedExport = async (exportId) => {
  const token = localStorage.getItem('fundelite_token');
  const response = await fetch(`${API_URL}/api/collection-markup/exports/${exportId}/download`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Download failed');
  }
  
  return response.blob();
};


// ==================== SEND FUNCTIONS ====================

/**
 * Extract recipient info from the letter
 */
export const extractRecipient = async (documentId, extractedText = null) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/extract-recipient`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      extracted_text: extractedText
    })
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Recipient extraction failed');
  }
  
  return response.json();
};

/**
 * Create a send draft (does not send immediately)
 */
export const createSendDraft = async (documentId, exportId, recipient, subject = null, body = null) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/send`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      export_id: exportId,
      recipient,
      subject,
      body
    })
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to create send draft');
  }
  
  return response.json();
};

/**
 * Update a send draft before sending
 */
export const updateSendDraft = async (sendId, updates) => {
  const response = await fetch(`${API_URL}/api/collection-markup/send/${sendId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to update send draft');
  }
  
  return response.json();
};

/**
 * Execute the send action (actually sends the email)
 */
export const executeSend = async (sendId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/send/${sendId}/execute`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Send failed');
  }
  
  return response.json();
};

/**
 * Get send history for a document (audit trail)
 */
export const getSendHistory = async (documentId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/send-history`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get send history');
  }
  
  return response.json();
};

/**
 * Cancel a pending send
 */
export const cancelSend = async (sendId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/send/${sendId}/cancel`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Cancel failed');
  }
  
  return response.json();
};


// ==================== APPLY CHANGES / SNAPSHOT FUNCTIONS ====================

/**
 * Apply markup changes to create an applied snapshot
 * Required before Export/Print/Send
 */
export const applyChanges = async (documentId, annotations, changesSummary = null) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/apply-changes`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      annotations,
      changes_summary: changesSummary
    })
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Apply changes failed');
  }
  
  return response.json();
};

/**
 * Get the latest applied snapshot
 */
export const getLatestSnapshot = async (documentId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/latest-snapshot`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to get snapshot');
  }
  
  return response.json();
};

/**
 * List all applied snapshots (version history)
 */
export const listSnapshots = async (documentId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/snapshots`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to list snapshots');
  }
  
  return response.json();
};


// ==================== POSTAL MAIL FUNCTIONS ====================

/**
 * Validate address via PostGrid
 */
export const validateAddress = async (address) => {
  const response = await fetch(`${API_URL}/api/collection-markup/address/validate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(address)
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Address validation failed');
  }
  
  return response.json();
};

/**
 * Extract mailing address from letter
 */
export const extractMailingAddress = async (documentId) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/extract-mailing-address`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Address extraction failed');
  }
  
  return response.json();
};

/**
 * Create postal mail send draft
 */
export const createPostalSend = async (documentId, snapshotId, recipient) => {
  const response = await fetch(`${API_URL}/api/collection-markup/documents/${documentId}/postal-send`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      snapshot_id: snapshotId,
      recipient
    })
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Failed to create postal send');
  }
  
  return response.json();
};

/**
 * Execute postal mail send via PostGrid
 */
export const executePostalSend = async (sendId, senderInfo) => {
  const response = await fetch(`${API_URL}/api/collection-markup/send/${sendId}/postal-execute`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      sender_info: senderInfo
    })
  });
  
  if (!response.ok) {
    await handleApiError(response, 'Postal send failed');
  }
  
  return response.json();
};


// ==================== DEFAULT EXPORT ====================

export default {
  uploadDocument,
  listDocuments,
  getDocument,
  deleteDocument,
  startAnalysis,
  getAnalysisStatus,
  pollAnalysisUntilComplete,
  getDocumentPreview,
  updateFinding,
  mergeFindings,
  addManualFinding,
  exportDocument,
  getIssueLibrary,
  getPermissions,
  createReviewedExport,
  listReviewedExports,
  downloadReviewedExport,
  extractRecipient,
  createSendDraft,
  updateSendDraft,
  executeSend,
  getSendHistory,
  cancelSend,
  applyChanges,
  getLatestSnapshot,
  listSnapshots,
  validateAddress,
  extractMailingAddress,
  createPostalSend,
  executePostalSend
};
